import React, { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import CloseIcon from "@mui/icons-material/Close";
import { validateEmail } from "../../utils/utilityFunctions";

const useStyles = makeStyles()((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  criteriaBox: {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
  },
  progress: {
    marginRight: theme.spacing(1),
  },
}));

function NominateFoundingPartnerModal({ open, onClose }) {
  const { classes } = useStyles();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const validateForm = () => {
    const newErrors = {};

    if (!firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!company.trim()) {
      newErrors.company = "Company name is required";
    }
    if (!website.trim()) {
      newErrors.website = "Website is required";
    } else if (!website.match(/^https?:\/\/.+/i)) {
      newErrors.website =
        "Please enter a valid URL (e.g., https://example.com)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post("/api/foundingPartners/nominate", {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        company: company.trim(),
        website: website.trim(),
      });

      if (response.data.status === "success") {
        setSuccessMessage(
          "Nomination submitted successfully! Oliver will review it soon."
        );
        // Clear form
        setFirstName("");
        setLastName("");
        setEmail("");
        setCompany("");
        setWebsite("");
        setErrors({});

        // Close modal after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setErrorMessage("Failed to submit nomination. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting nomination:", error);
      setErrorMessage(
        error.response?.data?.error ||
          "An error occurred. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFirstName("");
    setLastName("");
    setEmail("");
    setCompany("");
    setWebsite("");
    setErrors({});
    setSuccessMessage("");
    setErrorMessage("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="nominate-dialog"
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h3">Nominate a Founding Partner</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="body1" paragraph>
          Know someone who would be a great fit for the Founding Partner
          program? Nominate them here!
        </Typography>

        <div className={classes.criteriaBox}>
          <Typography variant="h4" gutterBottom>
            Ideal Candidate
          </Typography>
          <Typography variant="body2" paragraph>
            We're looking for people who are:
          </Typography>
          <Typography variant="body2" component="div">
            • Running a real business with active social media marketing
            <br />
            • Using social media for genuine business cases and growth
            <br />
            • Passionate about helping others succeed
            <br />• In the arena, actively working on their craft
          </Typography>
        </div>

        {successMessage && (
          <Alert severity="success" sx={{ marginBottom: 2 }}>
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="First Name"
            variant="outlined"
            fullWidth
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={!!errors.firstName}
            helperText={errors.firstName}
            className={classes.textField}
            disabled={loading}
          />

          <TextField
            label="Last Name"
            variant="outlined"
            fullWidth
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={!!errors.lastName}
            helperText={errors.lastName}
            className={classes.textField}
            disabled={loading}
          />

          <TextField
            label="Email Address"
            variant="outlined"
            fullWidth
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            className={classes.textField}
            disabled={loading}
          />

          <TextField
            label="Company Name"
            variant="outlined"
            fullWidth
            required
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            error={!!errors.company}
            helperText={errors.company}
            className={classes.textField}
            disabled={loading}
          />

          <TextField
            label="Website"
            variant="outlined"
            fullWidth
            required
            placeholder="https://example.com"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            error={!!errors.website}
            helperText={errors.website}
            className={classes.textField}
            disabled={loading}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading && (
            <CircularProgress size={20} className={classes.progress} />
          )}
          Nominate for Founding Partner
        </Button>
      </DialogActions>
    </Dialog>
  );
}

NominateFoundingPartnerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NominateFoundingPartnerModal;





